import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Landmark, ShieldCheck, Clock3, Wallet, IndianRupee, FileText, Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  getBankDetails,
  saveBankDetails,
  saveDocuments,
  getEarningsSummary,
  getEarningsEntries,
  getReportSub,
  subscribeReport,
  sendReportNow,
  errMsg,
} from "@/lib/api";
import { inr, shortId, fmtDateTime } from "@/lib/format";
import { PageHeader } from "@/components/common";
import { cn } from "@/lib/utils";

const Stat = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="rounded-md border border-border bg-card p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground font-semibold">{label}</span>
      <span className={cn("h-8 w-8 rounded-md grid place-items-center", accent)}>
        <Icon className="h-4 w-4" />
      </span>
    </div>
    <p className="font-display font-black text-3xl mt-3 leading-none">{value}</p>
  </div>
);

const Panel = ({ title, icon: Icon, children, footer }) => (
  <div className="rounded-md border border-border bg-card">
    <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border">
      <Icon className="h-4 w-4 text-primary" />
      <h3 className="font-display font-bold text-lg">{title}</h3>
    </div>
    <div className="p-5 space-y-4">{children}</div>
    {footer && <div className="px-5 py-4 border-t border-border">{footer}</div>}
  </div>
);

export default function Payouts() {
  const qc = useQueryClient();
  const summary = useQuery({ queryKey: ["earnings-summary"], queryFn: async () => (await getEarningsSummary()).data });
  const entries = useQuery({ queryKey: ["earnings-entries"], queryFn: async () => (await getEarningsEntries()).data });
  const bank = useQuery({ queryKey: ["bank-details"], queryFn: async () => (await getBankDetails()).data });

  const [form, setForm] = useState({ account_number: "", ifsc: "", holder_name: "", upi_vpa: "" });
  const [docs, setDocs] = useState({ fssai_number: "", gstin: "", pan_number: "" });
  const [reportEmail, setReportEmail] = useState("");
  const [weekly, setWeekly] = useState(true);

  const reportQ = useQuery({ queryKey: ["report-sub"], queryFn: async () => (await getReportSub()).data });
  useEffect(() => {
    if (reportQ.data) {
      setReportEmail(reportQ.data.email || "");
      setWeekly(reportQ.data.weekly_enabled ?? true);
    }
  }, [reportQ.data]);

  const subMut = useMutation({
    mutationFn: () => subscribeReport({ email: reportEmail, weekly_enabled: weekly }),
    onSuccess: () => { toast.success("Weekly report settings saved"); qc.invalidateQueries({ queryKey: ["report-sub"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const sendNowMut = useMutation({
    mutationFn: () => sendReportNow(),
    onSuccess: () => toast.success("Report emailed to " + reportEmail),
    onError: (e) => toast.error(errMsg(e)),
  });

  useEffect(() => {
    if (bank.data) {
      setForm({
        account_number: bank.data.account_number || "",
        ifsc: bank.data.ifsc || "",
        holder_name: bank.data.holder_name || "",
        upi_vpa: bank.data.upi_vpa || "",
      });
    }
  }, [bank.data]);

  const bankMut = useMutation({
    mutationFn: () => saveBankDetails(form),
    onSuccess: () => {
      toast.success("Bank details saved");
      qc.invalidateQueries({ queryKey: ["bank-details"] });
      qc.invalidateQueries({ queryKey: ["earnings-summary"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const docMut = useMutation({
    mutationFn: () => saveDocuments(docs),
    onSuccess: () => {
      toast.success("Documents submitted for verification");
      qc.invalidateQueries({ queryKey: ["earnings-summary"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const s = summary.data || {};

  return (
    <div className="p-5 sm:p-8 max-w-5xl">
      <PageHeader title="Payouts" subtitle="Earnings, bank account & KYC" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat icon={Clock3} label="Pending" value={inr(s.pending)} accent="bg-amber-100 text-amber-700" testid="stat-pending" />
        <Stat icon={IndianRupee} label="This week" value={inr(s.this_week)} accent="bg-emerald-100 text-emerald-700" testid="stat-week" />
        <Stat icon={Wallet} label="Paid lifetime" value={inr(s.paid_lifetime)} accent="bg-blue-100 text-blue-700" testid="stat-paid" />
        <Stat icon={ShieldCheck} label="KYC" value={(s.kyc_status || "—").replace(/_/g, " ")} accent="bg-violet-100 text-violet-700" testid="stat-kyc" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <Panel
          title="Bank / UPI details"
          icon={Landmark}
          footer={
            <Button onClick={() => bankMut.mutate()} disabled={bankMut.isPending} data-testid="save-bank-btn" className="font-semibold">
              {bankMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save bank details"}
            </Button>
          }
        >
          <div className="space-y-1.5">
            <Label>Account holder name</Label>
            <Input data-testid="bank-holder" value={form.holder_name} onChange={(e) => setForm({ ...form, holder_name: e.target.value })} placeholder="As per bank records" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Account number</Label>
              <Input data-testid="bank-account" value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>IFSC</Label>
              <Input data-testid="bank-ifsc" value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value.toUpperCase() })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>UPI ID (optional)</Label>
            <Input data-testid="bank-upi" value={form.upi_vpa} onChange={(e) => setForm({ ...form, upi_vpa: e.target.value })} placeholder="name@bank" />
          </div>
        </Panel>

        <Panel
          title="KYC documents"
          icon={FileText}
          footer={
            <Button onClick={() => docMut.mutate()} disabled={docMut.isPending} data-testid="save-docs-btn" variant="outline" className="font-semibold">
              {docMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit documents"}
            </Button>
          }
        >
          <div className="space-y-1.5">
            <Label>FSSAI licence number</Label>
            <Input data-testid="doc-fssai" value={docs.fssai_number} onChange={(e) => setDocs({ ...docs, fssai_number: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>GSTIN</Label>
              <Input data-testid="doc-gstin" value={docs.gstin} onChange={(e) => setDocs({ ...docs, gstin: e.target.value.toUpperCase() })} />
            </div>
            <div className="space-y-1.5">
              <Label>PAN</Label>
              <Input data-testid="doc-pan" value={docs.pan_number} onChange={(e) => setDocs({ ...docs, pan_number: e.target.value.toUpperCase() })} />
            </div>
          </div>
        </Panel>
      </div>

      <div className="mb-6">
        <Panel
          title="Weekly payout report"
          icon={Mail}
          footer={
            <div className="flex items-center gap-3">
              <Button onClick={() => subMut.mutate()} disabled={subMut.isPending || !reportEmail} data-testid="save-report-btn" className="font-semibold">
                {subMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}
              </Button>
              <Button
                variant="outline"
                onClick={() => sendNowMut.mutate()}
                disabled={sendNowMut.isPending || !reportQ.data?.email}
                data-testid="send-report-now-btn"
                className="font-semibold"
              >
                {sendNowMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Send className="h-4 w-4 mr-1.5" /> Email me now</>)}
              </Button>
            </div>
          }
        >
          <p className="text-sm text-muted-foreground -mt-1">
            Get a clean earnings & payout summary emailed to you every Monday morning.
          </p>
          <div className="space-y-1.5">
            <Label>Email address</Label>
            <Input
              type="email"
              data-testid="report-email"
              value={reportEmail}
              onChange={(e) => setReportEmail(e.target.value)}
              placeholder="you@shop.com"
            />
          </div>
          <label className="flex items-center gap-3 text-sm cursor-pointer">
            <Switch checked={weekly} onCheckedChange={setWeekly} data-testid="report-weekly-toggle" />
            Send my summary automatically every Monday
          </label>
        </Panel>
      </div>

      <div className="rounded-md border border-border bg-card">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="font-display font-bold text-lg">Payout entries</h3>
          <p className="text-sm text-muted-foreground">Per-order settlements</p>
        </div>
        <div className="divide-y divide-border max-h-[420px] overflow-y-auto">
          {entries.isLoading ? (
            <div className="p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : (entries.data || []).length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No entries yet.</div>
          ) : (
            (entries.data || []).map((e) => (
              <div key={e.id} className="flex items-center justify-between px-5 py-3.5" data-testid="payout-entry">
                <div className="min-w-0">
                  <p className="font-medium text-sm">
                    {shortId(e.order_id)} · {e.order_info?.restaurant_name || "Order"}
                  </p>
                  <p className="text-xs text-muted-foreground">{fmtDateTime(e.created_at)}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono font-semibold text-primary">{inr(e.amount)}</p>
                  <span className={cn("text-xs font-medium", e.status === "paid" ? "text-emerald-600" : "text-amber-600")}>
                    {(e.status || "").toUpperCase()}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
